import { IdAndFaceValidationVerbiage } from 'idmission-web-sdk/dist/components/customer_flows/IdAndFaceValidation';
import { useMemo } from 'react';

import { flattenedToNested } from '@/utils/objectUtil';

import { useTranslate } from '../useTranslate';

const DOC_TYPES = ['idCardFront', 'idCardBack', 'passport'] as const;

const DOCUMENT_VERBIAGE_DEFAULTS = {
  instructionText: 'Scan the front of ID',
  processingIdCardText: 'ID card front captured.',
  capturingText: 'Capturing…',
  captureFailedText: 'Capture failed!',
  guidanceSatisfiedText: 'ID Capture Successful',
  guidancePleaseFlipText: 'ID card front detected - please flip your ID card',
  guidanceBackDetectedFirstText:
    'ID card back detected - please flip your ID card',
  guidanceTooBlurryText: 'Document out of focus - try improving the lighting',
  guidanceNotCenteredText: 'Document is not centered',
  guidanceTooCloseText: 'Document too close, please back up',
  guidanceNotStableText: 'Document is not stable',
  guidanceNotDetectedText: 'Document not detected',
  guidanceIdCardNotAllowedText:
    'ID card detected, please scan a passport instead',
  guidancePassportNotAllowedText:
    'Passport detected, please scan an ID card instead',
  'overrideWrongDocumentTypeGuidanceDialog.messageText':
    'We are having trouble identifying the correct side of your id, do you want to continue with capture anyway?',
  'overrideWrongDocumentTypeGuidanceDialog.buttonText': 'OK',
} as const;

type DocumentVerbiageKey = keyof typeof DOCUMENT_VERBIAGE_DEFAULTS;

const DOCUMENT_VERBIAGE_KEYS = Object.keys(
  DOCUMENT_VERBIAGE_DEFAULTS,
) as DocumentVerbiageKey[];

function buildDocumentVerbiage(
  t: ReturnType<typeof useTranslate>['t'],
): Record<string, string> {
  const flatten: Record<string, string> = {};

  DOC_TYPES.forEach(docType => {
    DOCUMENT_VERBIAGE_KEYS.forEach(key => {
      const fullKey = `idCapture.${docType}.${key}`;
      flatten[fullKey] = t(fullKey, {
        defaultValue: DOCUMENT_VERBIAGE_DEFAULTS[key],
      });
    });
  });

  return flatten;
}

export function useIdmissionVerbiage() {
  const { t } = useTranslate(['idmission']);

  const verbiage = useMemo(() => {
    const flatten: Record<string, string> = {
      'idCapture.uploadOrCaptureScreen.captureWithCameraText': t(
        'idCapture.uploadOrCaptureScreen.captureWithCameraText',
        { defaultValue: 'Capture with Camera' },
      ),
      'idCapture.uploadOrCaptureScreen.orText': t(
        'idCapture.uploadOrCaptureScreen.orText',
        { defaultValue: 'OR' },
      ),
      'idCapture.uploadOrCaptureScreen.uploadFromStorageText': t(
        'idCapture.uploadOrCaptureScreen.uploadFromStorageText',
        { defaultValue: 'Upload from Storage' },
      ),
      'idCapture.uploadOrCaptureScreen.selectIdTypeText': t(
        'idCapture.uploadOrCaptureScreen.selectIdTypeText',
        { defaultValue: 'Select ID Type' },
      ),
      'idCapture.uploadOrCaptureScreen.passportText': t(
        'idCapture.uploadOrCaptureScreen.passportText',
        { defaultValue: 'Passport' },
      ),
      'idCapture.uploadOrCaptureScreen.idCardText': t(
        'idCapture.uploadOrCaptureScreen.idCardText',
        { defaultValue: 'ID Card' },
      ),
      'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.uploadIdFrontText':
        t(
          'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.uploadIdFrontText',
          { defaultValue: 'Upload ID Front' },
        ),
      'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.uploadIdBackText':
        t(
          'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.uploadIdBackText',
          { defaultValue: 'Upload ID Back' },
        ),
      'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.uploadIdBackHelpTextIfSinglePageAllowed':
        t(
          'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.uploadIdBackHelpTextIfSinglePageAllowed',
          {
            defaultValue:
              'Upload the back of the ID if it is not included in the front image',
          },
        ),
      'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.loadingText': t(
        'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.loadingText',
        { defaultValue: 'Loading…' },
      ),
      'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.continueText': t(
        'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.continueText',
        { defaultValue: 'Continue' },
      ),
      'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.cancelText': t(
        'idCapture.uploadOrCaptureScreen.idDocumentUploadScreen.cancelText',
        { defaultValue: 'Cancel' },
      ),
      'idCapture.uploadOrCaptureScreen.passportUploadScreen.uploadPassportText':
        t(
          'idCapture.uploadOrCaptureScreen.passportUploadScreen.uploadPassportText',
          { defaultValue: 'Upload Passport' },
        ),
      'idCapture.uploadOrCaptureScreen.passportUploadScreen.loadingText': t(
        'idCapture.uploadOrCaptureScreen.passportUploadScreen.loadingText',
        { defaultValue: 'Loading…' },
      ),
      'idCapture.uploadOrCaptureScreen.passportUploadScreen.continueText': t(
        'idCapture.uploadOrCaptureScreen.passportUploadScreen.continueText',
        { defaultValue: 'Continue' },
      ),
      'idCapture.uploadOrCaptureScreen.passportUploadScreen.cancelText': t(
        'idCapture.uploadOrCaptureScreen.passportUploadScreen.cancelText',
        { defaultValue: 'Cancel' },
      ),
      'idCapture.loadingOverlay.headingText': t(
        'idCapture.loadingOverlay.headingText',
        { defaultValue: 'Use your device camera to capture your ID' },
      ),
      'idCapture.loadingOverlay.useContinuityCameraText': t(
        'idCapture.loadingOverlay.useContinuityCameraText',
        { defaultValue: 'Use your iPhone as a webcam' },
      ),
      'idCapture.loadingOverlay.downloadingText': t(
        'idCapture.loadingOverlay.downloadingText',
        { defaultValue: 'Downloading…' },
      ),
      'idCapture.loadingOverlay.modelsWarmingUpText': t(
        'idCapture.loadingOverlay.modelsWarmingUpText',
        { defaultValue: 'Models warming up…' },
      ),
      'idCapture.loadingOverlay.modelsReadyText': t(
        'idCapture.loadingOverlay.modelsReadyText',
        { defaultValue: 'Guided capture experience ready' },
      ),
      'idCapture.loadingOverlay.cameraInitializingText': t(
        'idCapture.loadingOverlay.cameraInitializingText',
        { defaultValue: 'Camera initializing…' },
      ),
      'idCapture.loadingOverlay.continueText': t(
        'idCapture.loadingOverlay.continueText',
        { defaultValue: 'Continue' },
      ),
      'idCapture.loadingOverlay.pleaseWaitText': t(
        'idCapture.loadingOverlay.pleaseWaitText',
        { defaultValue: 'Please wait…' },
      ),
      'idCapture.loadingOverlay.cameraInitializedText': t(
        'idCapture.loadingOverlay.cameraInitializedText',
        { defaultValue: 'Camera ready' },
      ),
      'idCapture.loadingOverlay.cameraAccessDenied.headingText': t(
        'idCapture.loadingOverlay.cameraAccessDenied.headingText',
        { defaultValue: 'Your camera permission is disabled' },
      ),
      'idCapture.loadingOverlay.cameraAccessDenied.descriptionText': t(
        'idCapture.loadingOverlay.cameraAccessDenied.descriptionText',
        {
          defaultValue:
            'This application requires access to your camera to continue. Please accept the permission once prompted by the browser. If the browser does not prompt for camera permissions, you must go to settings and provide camera access to the current browser.',
        },
      ),
      'idCapture.loadingOverlay.cameraAccessDenied.retryBtnText': t(
        'idCapture.loadingOverlay.cameraAccessDenied.retryBtnText',
        { defaultValue: 'Retry' },
      ),
      'idCapture.loadingOverlay.sessionCheckingText': t(
        'idCapture.loadingOverlay.sessionCheckingText',
        { defaultValue: 'Authenticating…' },
      ),
      ...buildDocumentVerbiage(t),
      'idCapture.flipIdPrompt.instructionText': t(
        'idCapture.flipIdPrompt.instructionText',
        { defaultValue: 'Please flip your ID card…' },
      ),
      'idCapture.success.headingText': t('idCapture.success.headingText', {
        defaultValue: 'ID Capture Successful',
      }),
      'idCapture.success.submitText': t('idCapture.success.submitText', {
        defaultValue: 'Submit',
      }),
      'idCapture.success.submittingText': t(
        'idCapture.success.submittingText',
        { defaultValue: 'Submitting…' },
      ),
      'idCapture.success.retryText': t('idCapture.success.retryText', {
        defaultValue: 'Retry',
      }),
      'idCapture.fallback.headingText': t('idCapture.fallback.headingText', {
        defaultValue: 'We’re having some trouble.',
      }),
      'idCapture.fallback.descriptionText': t(
        'idCapture.fallback.descriptionText',
        {
          defaultValue:
            'On-device capture guidance failed, please capture a photo of the ID card manually.',
        },
      ),
      'idCapture.fallback.firstInstructionText': t(
        'idCapture.fallback.firstInstructionText',
        { defaultValue: 'Please capture the front of your ID card.' },
      ),
      'idCapture.fallback.secondInstructionText': t(
        'idCapture.fallback.secondInstructionText',
        { defaultValue: 'Please capture the back of your ID card.' },
      ),
      'idCapture.fallback.thirdInstructionText': t(
        'idCapture.fallback.thirdInstructionText',
        { defaultValue: 'Please capture the ID page of your passport.' },
      ),
      'idCapture.fallback.captureBtnText': t(
        'idCapture.fallback.captureBtnText',
        { defaultValue: 'Capture' },
      ),
      'idCapture.fallback.captureMoreBtnText': t(
        'idCapture.fallback.captureMoreBtnText',
        { defaultValue: 'Capture ID page of passport' },
      ),
      'idCapture.fallback.doneBtnText': t('idCapture.fallback.doneBtnText', {
        defaultValue: 'Done',
      }),
      'faceLiveness.guidanceHoldStillText': t(
        'faceLiveness.guidanceHoldStillText',
        { defaultValue: 'Hold still for a few seconds…' },
      ),
      'faceLiveness.guidanceLookStraightText': t(
        'faceLiveness.guidanceLookStraightText',
        { defaultValue: 'Look straight into the camera…' },
      ),
      'faceLiveness.guidanceMoveBackText': t(
        'faceLiveness.guidanceMoveBackText',
        { defaultValue: 'Move back…' },
      ),
      'faceLiveness.guidanceMoveForwardText': t(
        'faceLiveness.guidanceMoveForwardText',
        { defaultValue: 'Move forward…' },
      ),
      'faceLiveness.guidanceMoveToCenterText': t(
        'faceLiveness.guidanceMoveToCenterText',
        { defaultValue: 'Move to the center…' },
      ),
      'faceLiveness.guidanceNoFaceDetectedText': t(
        'faceLiveness.guidanceNoFaceDetectedText',
        { defaultValue: 'Waiting for face to be detected…' },
      ),
      'faceLiveness.guidanceVisibilityTooLowText': t(
        'faceLiveness.guidanceVisibilityTooLowText',
        { defaultValue: 'XXX' },
      ),
      'faceLiveness.guidanceNotStableText': t(
        'faceLiveness.guidanceNotStableText',
        { defaultValue: 'Please hold still…' },
      ),
      'faceLiveness.guidanceManualCaptureText': t(
        'faceLiveness.guidanceManualCaptureText',
        {
          defaultValue:
            'Position your face in the circle and click to capture.',
        },
      ),
      'faceLiveness.manualCaptureBtnText': t(
        'faceLiveness.manualCaptureBtnText',
        { defaultValue: 'Capture' },
      ),
      'faceLiveness.guidanceRemoveEyeCoveringsText': t(
        'faceLiveness.guidanceRemoveEyeCoveringsText',
        { defaultValue: 'Remove Sunglasses & Hat' },
      ),
      'faceLiveness.guidanceRemoveHeadCoveringsText': t(
        'faceLiveness.guidanceRemoveHeadCoveringsText',
        { defaultValue: 'Remove Head Coverings' },
      ),
      'faceLiveness.guidanceRemoveMaskText': t(
        'faceLiveness.guidanceRemoveMaskText',
        { defaultValue: 'Remove Mask' },
      ),
      'faceLiveness.progressPreviewText': t(
        'faceLiveness.progressPreviewText',
        { defaultValue: 'Processing…' },
      ),
      'faceLiveness.guidanceLivenessCheckFailedText': t(
        'faceLiveness.guidanceLivenessCheckFailedText',
        { defaultValue: 'Could not verify your face.' },
      ),
      'faceLiveness.guidanceLivenessCheckErrorText': t(
        'faceLiveness.guidanceLivenessCheckErrorText',
        { defaultValue: 'An error occurred while verifying your face.' },
      ),
      'faceLiveness.loadingOverlay.headingText': t(
        'faceLiveness.loadingOverlay.headingText',
        { defaultValue: 'Use your device camera to capture your face' },
      ),
      'faceLiveness.loadingOverlay.removeEyeCoveringsText': t(
        'faceLiveness.loadingOverlay.removeEyeCoveringsText',
        { defaultValue: 'Remove Sunglasses & Hat' },
      ),
      'faceLiveness.loadingOverlay.avoidExcessiveBacklightingText': t(
        'faceLiveness.loadingOverlay.avoidExcessiveBacklightingText',
        { defaultValue: 'Avoid Excessive Backlighting' },
      ),
      'faceLiveness.loadingOverlay.continueText': t(
        'faceLiveness.loadingOverlay.continueText',
        { defaultValue: 'Continue' },
      ),
      'faceLiveness.loadingOverlay.pleaseWaitText': t(
        'faceLiveness.loadingOverlay.pleaseWaitText',
        { defaultValue: 'Please wait…' },
      ),
      'faceLiveness.loadingOverlay.cameraInitializingText': t(
        'faceLiveness.loadingOverlay.cameraInitializingText',
        { defaultValue: 'Camera initializing…' },
      ),
      'faceLiveness.loadingOverlay.downloadingText': t(
        'faceLiveness.loadingOverlay.downloadingText',
        { defaultValue: 'Downloading…' },
      ),
      'faceLiveness.loadingOverlay.modelsWarmingUpText': t(
        'faceLiveness.loadingOverlay.modelsWarmingUpText',
        { defaultValue: 'Models warming up…' },
      ),
      'faceLiveness.loadingOverlay.modelsReadyText': t(
        'faceLiveness.loadingOverlay.modelsReadyText',
        { defaultValue: 'Guided capture experience ready' },
      ),
      'faceLiveness.loadingOverlay.cameraInitializedText': t(
        'faceLiveness.loadingOverlay.cameraInitializedText',
        { defaultValue: 'Camera ready' },
      ),
      'faceLiveness.loadingOverlay.cameraAccessDenied.headingText': t(
        'faceLiveness.loadingOverlay.cameraAccessDenied.headingText',
        { defaultValue: 'Your camera permission is disabled' },
      ),
      'faceLiveness.loadingOverlay.cameraAccessDenied.descriptionText': t(
        'faceLiveness.loadingOverlay.cameraAccessDenied.descriptionText',
        {
          defaultValue:
            'This application requires access to your camera to continue. Please accept the permission once prompted by the browser. If the browser does not prompt for camera permissions, you must go to settings and provide camera access to the current browser.',
        },
      ),
      'faceLiveness.loadingOverlay.cameraAccessDenied.retryBtnText': t(
        'faceLiveness.loadingOverlay.cameraAccessDenied.retryBtnText',
        { defaultValue: 'Retry' },
      ),
      'faceLiveness.loadingOverlay.microphoneAccessDenied.headingText': t(
        'faceLiveness.loadingOverlay.microphoneAccessDenied.headingText',
        { defaultValue: 'Your microphone permission is disabled' },
      ),
      'faceLiveness.loadingOverlay.microphoneAccessDenied.descriptionText': t(
        'faceLiveness.loadingOverlay.microphoneAccessDenied.descriptionText',
        {
          defaultValue:
            'This application requires access to your microphone to continue. Please accept the permission once prompted by the browser. If the browser does not prompt for microphone permissions, you must go to settings and provide microphone access to the current browser.',
        },
      ),
      'faceLiveness.loadingOverlay.microphoneAccessDenied.retryBtnText': t(
        'faceLiveness.loadingOverlay.microphoneAccessDenied.retryBtnText',
        { defaultValue: 'Retry' },
      ),
      'faceLiveness.loadingOverlay.sessionCheckingText': t(
        'faceLiveness.loadingOverlay.sessionCheckingText',
        { defaultValue: 'Authenticating…' },
      ),
      'faceLiveness.success.headingText': t(
        'faceLiveness.success.headingText',
        { defaultValue: 'Face liveness has been verified!' },
      ),
      'faceLiveness.success.retryBtnText': t(
        'faceLiveness.success.retryBtnText',
        { defaultValue: 'Retry' },
      ),
      'faceLiveness.success.doneBtnText': t(
        'faceLiveness.success.doneBtnText',
        { defaultValue: 'Done' },
      ),
      'faceLiveness.failure.headingText': t(
        'faceLiveness.failure.headingText',
        { defaultValue: 'Live face not detected, please try again' },
      ),
      'faceLiveness.failure.retryBtnText': t(
        'faceLiveness.failure.retryBtnText',
        { defaultValue: 'Retry' },
      ),
      'faceLiveness.failure.exitBtnText': t(
        'faceLiveness.failure.exitBtnText',
        { defaultValue: 'Exit' },
      ),
      'additionalDocumentCapture.headingText': t(
        'additionalDocumentCapture.headingText',
        { defaultValue: 'Additional document capture' },
      ),
      'additionalDocumentCapture.captureBtnText': t(
        'additionalDocumentCapture.captureBtnText',
        { defaultValue: 'Capture' },
      ),
      'additionalDocumentCapture.nextBtnText': t(
        'additionalDocumentCapture.nextBtnText',
        { defaultValue: 'Next' },
      ),
      'signatureCapture.headingText': t('signatureCapture.headingText', {
        defaultValue: 'Please sign the box below',
      }),
      'signatureCapture.acceptBtnText': t('signatureCapture.acceptBtnText', {
        defaultValue: 'Accept',
      }),
      'signatureCapture.clearBtnText': t('signatureCapture.clearBtnText', {
        defaultValue: 'Clear',
      }),
      'videoSignatureCapture.guidanceMessageText': t(
        'videoSignatureCapture.guidanceMessageText',
        { defaultValue: 'Please sign the box below' },
      ),
      'videoSignatureCapture.guides.signaturePadEmptyText': t(
        'videoSignatureCapture.guides.signaturePadEmptyText',
        { defaultValue: '' },
      ),
      'videoSignatureCapture.guides.headTrackingUnsatisfiedText': t(
        'videoSignatureCapture.guides.headTrackingUnsatisfiedText',
        { defaultValue: '' },
      ),
      'videoSignatureCapture.guides.clearSignatureBtnText': t(
        'videoSignatureCapture.guides.clearSignatureBtnText',
        { defaultValue: '' },
      ),
      'videoSignatureCapture.guides.acceptSignatureBtnText': t(
        'videoSignatureCapture.guides.acceptSignatureBtnText',
        { defaultValue: '' },
      ),
      'videoSignatureCapture.guides.signHereText': t(
        'videoSignatureCapture.guides.signHereText',
        { defaultValue: '' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceHoldStillText': t(
        'videoSignatureCapture.faceLiveness.guidanceHoldStillText',
        { defaultValue: 'Hold still for a few seconds…' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceLookStraightText': t(
        'videoSignatureCapture.faceLiveness.guidanceLookStraightText',
        { defaultValue: 'Look straight into the camera…' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceMoveBackText': t(
        'videoSignatureCapture.faceLiveness.guidanceMoveBackText',
        { defaultValue: 'Move back…' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceMoveForwardText': t(
        'videoSignatureCapture.faceLiveness.guidanceMoveForwardText',
        { defaultValue: 'Move forward…' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceMoveToCenterText': t(
        'videoSignatureCapture.faceLiveness.guidanceMoveToCenterText',
        { defaultValue: 'Move to the center…' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceNoFaceDetectedText': t(
        'videoSignatureCapture.faceLiveness.guidanceNoFaceDetectedText',
        { defaultValue: 'Waiting for face to be detected…' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceVisibilityTooLowText': t(
        'videoSignatureCapture.faceLiveness.guidanceVisibilityTooLowText',
        { defaultValue: 'XXX' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceNotStableText': t(
        'videoSignatureCapture.faceLiveness.guidanceNotStableText',
        { defaultValue: 'Please hold still…' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceManualCaptureText': t(
        'videoSignatureCapture.faceLiveness.guidanceManualCaptureText',
        {
          defaultValue:
            'Position your face in the circle and click to capture.',
        },
      ),
      'videoSignatureCapture.faceLiveness.manualCaptureBtnText': t(
        'videoSignatureCapture.faceLiveness.manualCaptureBtnText',
        { defaultValue: 'Capture' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceRemoveEyeCoveringsText': t(
        'videoSignatureCapture.faceLiveness.guidanceRemoveEyeCoveringsText',
        { defaultValue: 'Remove Sunglasses & Hat' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceRemoveHeadCoveringsText': t(
        'videoSignatureCapture.faceLiveness.guidanceRemoveHeadCoveringsText',
        { defaultValue: 'Remove Head Coverings' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceRemoveMaskText': t(
        'videoSignatureCapture.faceLiveness.guidanceRemoveMaskText',
        { defaultValue: 'Remove Mask' },
      ),
      'videoSignatureCapture.faceLiveness.progressPreviewText': t(
        'videoSignatureCapture.faceLiveness.progressPreviewText',
        { defaultValue: 'Processing…' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceLivenessCheckFailedText': t(
        'videoSignatureCapture.faceLiveness.guidanceLivenessCheckFailedText',
        { defaultValue: 'Could not verify your face.' },
      ),
      'videoSignatureCapture.faceLiveness.guidanceLivenessCheckErrorText': t(
        'videoSignatureCapture.faceLiveness.guidanceLivenessCheckErrorText',
        { defaultValue: 'An error occurred while verifying your face.' },
      ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.headingText': t(
        'videoSignatureCapture.faceLiveness.loadingOverlay.headingText',
        { defaultValue: 'Use your device camera to capture your face' },
      ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.removeEyeCoveringsText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.removeEyeCoveringsText',
          { defaultValue: 'Remove Sunglasses & Hat' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.avoidExcessiveBacklightingText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.avoidExcessiveBacklightingText',
          { defaultValue: 'Avoid Excessive Backlighting' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.continueText': t(
        'videoSignatureCapture.faceLiveness.loadingOverlay.continueText',
        { defaultValue: 'Continue' },
      ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.pleaseWaitText': t(
        'videoSignatureCapture.faceLiveness.loadingOverlay.pleaseWaitText',
        { defaultValue: 'Please wait…' },
      ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.cameraInitializingText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.cameraInitializingText',
          { defaultValue: 'Camera initializing…' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.downloadingText': t(
        'videoSignatureCapture.faceLiveness.loadingOverlay.downloadingText',
        { defaultValue: 'Downloading…' },
      ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.modelsWarmingUpText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.modelsWarmingUpText',
          { defaultValue: 'Models warming up…' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.modelsReadyText': t(
        'videoSignatureCapture.faceLiveness.loadingOverlay.modelsReadyText',
        { defaultValue: 'Guided capture experience ready' },
      ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.cameraInitializedText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.cameraInitializedText',
          { defaultValue: 'Camera ready' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.cameraAccessDenied.headingText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.cameraAccessDenied.headingText',
          { defaultValue: 'Your camera permission is disabled' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.cameraAccessDenied.descriptionText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.cameraAccessDenied.descriptionText',
          {
            defaultValue:
              'This application requires access to your camera to continue. Please accept the permission once prompted by the browser. If the browser does not prompt for camera permissions, you must go to settings and provide camera access to the current browser.',
          },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.cameraAccessDenied.retryBtnText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.cameraAccessDenied.retryBtnText',
          { defaultValue: 'Retry' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.microphoneAccessDenied.headingText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.microphoneAccessDenied.headingText',
          { defaultValue: 'Your microphone permission is disabled' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.microphoneAccessDenied.descriptionText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.microphoneAccessDenied.descriptionText',
          {
            defaultValue:
              'This application requires access to your microphone to continue. Please accept the permission once prompted by the browser. If the browser does not prompt for microphone permissions, you must go to settings and provide microphone access to the current browser.',
          },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.microphoneAccessDenied.retryBtnText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.microphoneAccessDenied.retryBtnText',
          { defaultValue: 'Retry' },
        ),
      'videoSignatureCapture.faceLiveness.loadingOverlay.sessionCheckingText':
        t(
          'videoSignatureCapture.faceLiveness.loadingOverlay.sessionCheckingText',
          { defaultValue: 'Authenticating…' },
        ),
      'videoSignatureCapture.faceLiveness.success.headingText': t(
        'videoSignatureCapture.faceLiveness.success.headingText',
        { defaultValue: 'Face liveness has been verified!' },
      ),
      'videoSignatureCapture.faceLiveness.success.retryBtnText': t(
        'videoSignatureCapture.faceLiveness.success.retryBtnText',
        { defaultValue: 'Retry' },
      ),
      'videoSignatureCapture.faceLiveness.success.doneBtnText': t(
        'videoSignatureCapture.faceLiveness.success.doneBtnText',
        { defaultValue: 'Done' },
      ),
      'videoSignatureCapture.faceLiveness.failure.headingText': t(
        'videoSignatureCapture.faceLiveness.failure.headingText',
        { defaultValue: 'Live face not detected, please try again' },
      ),
      'videoSignatureCapture.faceLiveness.failure.retryBtnText': t(
        'videoSignatureCapture.faceLiveness.failure.retryBtnText',
        { defaultValue: 'Retry' },
      ),
      'videoSignatureCapture.faceLiveness.failure.exitBtnText': t(
        'videoSignatureCapture.faceLiveness.failure.exitBtnText',
        { defaultValue: 'Exit' },
      ),
      'videoSignatureCapture.success.headingText': t(
        'videoSignatureCapture.success.headingText',
        { defaultValue: 'Video signature has been successfully captured!' },
      ),
      'videoSignatureCapture.success.retryBtnText': t(
        'videoSignatureCapture.success.retryBtnText',
        { defaultValue: 'Retry' },
      ),
      'videoSignatureCapture.success.doneBtnText': t(
        'videoSignatureCapture.success.doneBtnText',
        { defaultValue: 'Done' },
      ),
    };

    return flattenedToNested(flatten) as IdAndFaceValidationVerbiage;
  }, [t]);

  return {
    verbiage,
  };
}
